const DB_NAME = 'GetPlanAppDB';
const STORE_NAME = 'GetPlanState';
const CACHE_STORE_NAME = 'PlanCache';
const WEATHER_GEN_STORE_NAME = 'WeatherGenState';
const DB_VERSION = 3;
const RECORD_KEY = 'draftState';
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

export interface GetPlanState {
  planName?: string;
  emailText: string;
  emailFile: File | null;
  dispatchMode: string;
}

export const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
        db.createObjectStore(CACHE_STORE_NAME);
      }
      if (!db.objectStoreNames.contains(WEATHER_GEN_STORE_NAME)) {
        db.createObjectStore(WEATHER_GEN_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const saveGetPlanState = async (state: GetPlanState): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const data = { state, timestamp: Date.now() };
    const request = store.put(data, RECORD_KEY);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const loadGetPlanState = async (): Promise<GetPlanState | null> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(RECORD_KEY);
    request.onsuccess = () => {
      const data = request.result;
      if (!data) return resolve(null);
      if (Date.now() - data.timestamp > EXPIRY_MS) {
        // Data expired, clear it
        clearGetPlanState();
        resolve(null);
      } else {
        resolve(data.state);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

export const clearGetPlanState = async (): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(RECORD_KEY);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const savePlanCache = async (cacheKey: string, plan: any): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CACHE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(CACHE_STORE_NAME);
    const data = { plan, timestamp: Date.now() };
    const request = store.put(data, cacheKey);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const getPlanCache = async (cacheKey: string): Promise<any | null> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CACHE_STORE_NAME, 'readonly');
    const store = tx.objectStore(CACHE_STORE_NAME);
    const request = store.get(cacheKey);
    request.onsuccess = () => {
      const data = request.result;
      if (!data) return resolve(null);
      if (Date.now() - data.timestamp > EXPIRY_MS) {
        resolve(null);
      } else {
        resolve(data.plan);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

export const saveWeatherGenState = async (state: any): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(WEATHER_GEN_STORE_NAME, 'readwrite');
    const store = tx.objectStore(WEATHER_GEN_STORE_NAME);
    const data = { state, timestamp: Date.now() };
    const request = store.put(data, RECORD_KEY);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const loadWeatherGenState = async (): Promise<any | null> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(WEATHER_GEN_STORE_NAME, 'readonly');
    const store = tx.objectStore(WEATHER_GEN_STORE_NAME);
    const request = store.get(RECORD_KEY);
    request.onsuccess = () => {
      const data = request.result;
      if (!data) return resolve(null);
      if (Date.now() - data.timestamp > EXPIRY_MS) {
        clearWeatherGenState();
        resolve(null);
      } else {
        resolve(data.state);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

export const clearWeatherGenState = async (): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(WEATHER_GEN_STORE_NAME, 'readwrite');
    const store = tx.objectStore(WEATHER_GEN_STORE_NAME);
    const request = store.delete(RECORD_KEY);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};
